/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import TextField from "../../inputs/TextField";
import NumberField from "../../inputs/NumberField";
import ToggleField from "../../inputs/ToggleField";
import { ADD_XSSI_CONNECTION_FORM_FIELDS } from "../../../constants";
import HexInput from "../../../ui/HexInput";
import { FORM_VALIDATION_TRANSLATIONS } from "../../../translations/util";
import TagList from "../../tagList/TagList";
import SearchableMultiSelect from "../../searchableMultiSelect/SearchableMultiSelect";

const set = new Set();

interface IConnectionData {
  "CONNECTION-ID": string;
  "RADIO-IPV4-ADDR": string;
  "RADIO-RFSS-ID"?: number;
  "RADIO-SYSTEM-ID"?: number;
  "RADIO-WACN-ID"?: number;
  "RADIO-RFSS-4-HEX-DIGIT": boolean;
  "IWF-RFSS-ID"?: number;
  "IWF-SYSTEM-ID"?: number;
  "IWF-WACN-ID"?: number;
  "RESPONSES-BEFORE-CONNECTED": number;
  "MISSED-RESPONSES-BEFORE-DISCONNECTED": number;
  "VOCODER-MODES": string[];
  "POLLING-ENABLED": boolean;
  "POLLING-INTERVAL-CONNECTED": number;
  "POLLING-INTERVAL-DISCONNECTED": number;
}

interface IProps {
  setReusedConnectionData: (val: any) => void;
  reusedConnectionData: IConnectionData;
  reusedConnectionId: string;
  isConnectionReused: boolean;
  isInValidNumberForField: any;
}

const transformSelectedItems = (vocoderList: string[]) => {
  return (
    vocoderList &&
    vocoderList.map((vocoder: string) => ({
      id: vocoder,
      text: vocoder,
    }))
  );
};

const ReuseConnection = ({
  setReusedConnectionData,
  reusedConnectionData,
  reusedConnectionId,
  isConnectionReused = false,
  isInValidNumberForField,
}: IProps) => {
  const [selectedVocoderMode, setSelectedVocoderMode] = useState<any[]>();

  useEffect(() => {
    if (reusedConnectionData && reusedConnectionId?.length >= 1) {
      setSelectedVocoderMode(
        transformSelectedItems(reusedConnectionData["VOCODER-MODES"])
      );
    } else {
      setSelectedVocoderMode([]);
    }
  }, [reusedConnectionData, reusedConnectionId]);

  return (
    <>
      {ADD_XSSI_CONNECTION_FORM_FIELDS.map((rows) => {
        return (
          <>
            {rows.fields.map((field) => {
              const { name } = field as {
                name: keyof IConnectionData;
                type: string;
              };
              switch (field.type) {
                case "TOGGLE":
                  return (
                    <ToggleField
                      labelText={field.label}
                      onChange={(val) =>
                        setReusedConnectionData({
                          ...reusedConnectionData,
                          [field.name]: val,
                        })
                      }
                      value={reusedConnectionData[name] as boolean}
                      tooltipText={field.tooltipText}
                      defaultToggled
                      disabled={reusedConnectionId?.length >= 1}
                    />
                  );
                case "NUMBER":
                  return (
                    <NumberField
                      onChange={(val) => {
                        set.add(field.name);
                        if (!val || !Number.isNaN(+val))
                          setReusedConnectionData({
                            ...reusedConnectionData,
                            [field.name]: val ? +val : undefined,
                          });
                      }}
                      label={field.label}
                      placeholder={field.label}
                      max={field.max || 1}
                      min={field.min || 1}
                      invalid={
                        set.has(field.name) &&
                        reusedConnectionData[name] !== 0 &&
                        !reusedConnectionData[name]
                      }
                      value={reusedConnectionData[name] as number}
                      invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                      tooltipText={field.tooltipText}
                      required
                      disabled={reusedConnectionId?.length >= 1}
                    />
                  );
                case "HEX":
                  return (
                    <HexInput
                      onChange={(val) => {
                        set.add(field.name);
                        setReusedConnectionData({
                          ...reusedConnectionData,
                          [field.name]: val || val === 0 ? +val : undefined,
                        });
                      }}
                      label={field.label}
                      placeholder={field.label}
                      max={field.max || 1}
                      min={field.min || 1}
                      invalid={
                        set.has(field.name) &&
                        isInValidNumberForField(
                          reusedConnectionData[name] as number,
                          field.min,
                          field.max
                        )
                      }
                      value={reusedConnectionData[name] as number}
                      invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                      tooltipText={field.tooltipText}
                      required
                      disabled={reusedConnectionId?.length >= 1}
                    />
                  );
                case "MULTISELECT":
                  return (
                    <div className="grid">
                      <SearchableMultiSelect
                        titleText={field.label}
                        placeholder={field.label}
                        items={[
                          { id: "FR", text: "FR" },
                          { id: "HR", text: "HR" },
                          { id: "Native", text: "Native" },
                          { id: "Phase 1", text: "Phase 1" },
                        ]}
                        tooltipText={field.tooltipText}
                        selectedItems={selectedVocoderMode}
                        disabled={
                          selectedVocoderMode &&
                          selectedVocoderMode?.length >= 1
                        }
                        required
                      />
                      <TagList
                        tagListArray={selectedVocoderMode?.map(
                          (obj: any) => obj?.text ?? obj["CHANNEL-ID"]
                        )}
                        disabled
                      />
                    </div>
                  );

                default:
                  return isConnectionReused &&
                    field.name !== "CONNECTION-ID" ? (
                    <TextField
                      onChange={(val) => {
                        set.add(field.name);
                        setReusedConnectionData({
                          ...reusedConnectionData,
                          [field.name]: val,
                        });
                      }}
                      placeholder={field.label}
                      value={reusedConnectionData[name] as string}
                      invalid={
                        set.has(field.name) &&
                        field.isInputInvalid?.(
                          reusedConnectionData[name] as string
                        )
                      }
                      invalidText={
                        field.invalidText ||
                        FORM_VALIDATION_TRANSLATIONS.required(field.label)
                      }
                      labelText={field.label}
                      tooltipText={field.tooltipText}
                      required
                      disabled={reusedConnectionId?.length >= 1}
                    />
                  ) : null;
              }
            })}
          </>
        );
      })}
    </>
  );
};

export default ReuseConnection;
